import { useState, type ReactElement } from 'react';
import type { SchemeDto } from '@gr/core/distribution/scheduling/dto';
import { useT } from '../../../../app/di';
import { Button } from '../../../../ui-kit/Button/Button';
import { FormAlert, FormSelectField, FormTextField, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';
import { Tabs } from '../../../../ui-kit/Tabs/Tabs';

export enum PlanningMode {
  Queue = 'queue',
  Exact = 'exact',
}

export interface PlanningValues {
  schemeId: string;
  /** Время в поясе блога, «ГГГГ-ММ-ДДTчч:мм»; только для точного времени. */
  at: string;
}

export interface PlanningFormProps {
  schemes: readonly SchemeDto[];
  timezone: string;
  /** Предложенное время для «точно в», в поясе блога. */
  defaultAt: string;
  onSubmit(mode: PlanningMode, values: PlanningValues): Promise<ServerOutcome>;
}

/** SCH-UC-2, SCH-UC-5: поставить пост в очередь по схеме или на точное время. */
export function PlanningForm({ schemes, timezone, defaultAt, onSubmit }: PlanningFormProps): ReactElement {
  const { t } = useT();
  const [mode, setMode] = useState<PlanningMode>(PlanningMode.Queue);
  const { form, submit } = useServerForm<PlanningValues>({ defaultValues: { schemeId: schemes[0]?.id ?? '', at: defaultAt }, fields: ['schemeId', 'at'], onSubmit: (values) => onSubmit(mode, values) });
  return (
    <form className="u-stack" onSubmit={submit} noValidate>
      <Tabs<PlanningMode>
        label={t('scheduling.panel.title')}
        items={[
          { key: PlanningMode.Queue, label: t('scheduling.panel.toQueue') },
          { key: PlanningMode.Exact, label: t('scheduling.panel.exact') },
        ]}
        active={mode}
        onChange={setMode}
      />
      <FormAlert form={form} />
      <FormSelectField control={form.control} name="schemeId" label={t('scheduling.panel.scheme')} options={schemes.map((s) => ({ value: s.id, label: s.name }))} />
      {mode === PlanningMode.Exact ? <FormTextField control={form.control} name="at" label={t('scheduling.panel.at')} type="datetime-local" hint={timezone} /> : null}
      <div>
        <Button type="submit" variant="primary" icon="calendar" loading={form.formState.isSubmitting}>
          {mode === PlanningMode.Queue ? t('scheduling.panel.toQueue') : t('scheduling.panel.schedule')}
        </Button>
      </div>
    </form>
  );
}
